import { api } from './authService';

export interface QueryParams {
  requestId?: string;
  tenantCode?: string;
  status?: string;
  stage?: string;
  chatBoxId?: string;
  messageType?: string;
  isSent?: boolean;
  limit?: number;
  offset?: number;
  orderBy?: string;
  orderDirection?: 'ASC' | 'DESC';
}

export interface PaginationInfo {
  total: number;
  limit: number;
  offset: number;
}

export interface QueryResponse<T> {
  success: boolean;
  data: T[];
  pagination: PaginationInfo;
  error?: string;
  message?: string;
}

export interface AuditEvent {
  id: number;
  request_id: string;
  stage: string;
  status: string;
  message?: string;
  error_message?: string;
  created_at: string;
}

export interface RequestMapping {
  id: number;
  request_id: string;
  tenant_code: string;
  chat_box_id?: string;
  status: string;
  ibuild_request_id?: string;
  created_at: string;
  updated_at: string;
}

export interface ResponseMessage {
  id: number;
  request_id: string;
  status: string;
  response_data?: string;
  error_message?: string;
  retry_count: number;
  created_at: string;
  updated_at: string;
}

export interface StreamMessage {
  id: number;
  request_id: string;
  message_type: string;
  content: string;
  is_sent: boolean;
  sequence_number?: number;
  created_at: string;
}

class IBuildProxyService {
  private getTenantCode(): string {
    return localStorage.getItem('currentTenant') || 'default';
  }

  /**
   * 查询审计事件
   */
  async getAuditEvents(params: QueryParams = {}): Promise<QueryResponse<AuditEvent>> {
    const tenantCode = this.getTenantCode();
    const queryParams = new URLSearchParams();
    
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        queryParams.append(key, String(value));
      }
    });

    const response = await api.get<QueryResponse<AuditEvent>>(
      `/${tenantCode}/ibuild-proxy/audit-events?${queryParams.toString()}`
    );
    return response.data;
  }

  /**
   * 查询请求映射
   */
  async getRequestMappings(params: QueryParams = {}): Promise<QueryResponse<RequestMapping>> {
    const tenantCode = this.getTenantCode();
    const queryParams = new URLSearchParams();
    
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        queryParams.append(key, String(value));
      }
    });

    const response = await api.get<QueryResponse<RequestMapping>>(
      `/${tenantCode}/ibuild-proxy/request-mappings?${queryParams.toString()}`
    );
    return response.data;
  }

  /**
   * 查询响应消息
   */
  async getResponseMessages(params: QueryParams = {}): Promise<QueryResponse<ResponseMessage>> {
    const tenantCode = this.getTenantCode();
    const queryParams = new URLSearchParams();
    
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        queryParams.append(key, String(value));
      }
    });

    const response = await api.get<QueryResponse<ResponseMessage>>(
      `/${tenantCode}/ibuild-proxy/response-messages?${queryParams.toString()}`
    );
    return response.data;
  }

  /**
   * 查询流式消息
   */
  async getStreamMessages(params: QueryParams = {}): Promise<QueryResponse<StreamMessage>> {
    const tenantCode = this.getTenantCode();
    const queryParams = new URLSearchParams();
    
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        queryParams.append(key, String(value));
      }
    });

    const response = await api.get<QueryResponse<StreamMessage>>(
      `/${tenantCode}/ibuild-proxy/stream-messages?${queryParams.toString()}`
    );
    return response.data;
  }

  /**
   * 获取 MQ 健康状态
   */
  async getMQHealth(): Promise<any> {
    const tenantCode = this.getTenantCode();
    const response = await api.get(`/${tenantCode}/ibuild-proxy/mq/health`);
    return response.data;
  }

  /**
   * 获取 MQ 详细状态
   */
  async getMQStatus(): Promise<any> {
    const tenantCode = this.getTenantCode();
    const response = await api.get(`/${tenantCode}/ibuild-proxy/mq/status`);
    return response.data;
  }

  /**
   * 重新连接 MQ
   */
  async reconnectMQ(): Promise<any> {
    const tenantCode = this.getTenantCode();
    const response = await api.post(`/${tenantCode}/ibuild-proxy/mq/reconnect`);
    return response.data;
  }

  /**
   * 关闭 MQ 连接
   */
  async closeMQ(): Promise<any> {
    const tenantCode = this.getTenantCode();
    const response = await api.post(`/${tenantCode}/ibuild-proxy/mq/close`);
    return response.data;
  }

  /**
   * 获取清理统计信息
   */
  async getCleanupStats(daysBefore: number = 30): Promise<any> {
    const tenantCode = this.getTenantCode();
    const response = await api.get(`/${tenantCode}/ibuild-proxy/cleanup/stats?daysBefore=${daysBefore}`);
    return response.data;
  }

  /**
   * 执行清理操作
   */
  async cleanup(daysBefore: number): Promise<any> {
    const tenantCode = this.getTenantCode();
    const response = await api.post(`/${tenantCode}/ibuild-proxy/cleanup`, { daysBefore });
    return response.data;
  }

  /**
   * 发送消息至 IBuild Agent
   */
  async sendAgentMessage(agentRequest: any): Promise<any> {
    const tenantCode = this.getTenantCode();
    const response = await api.post(`/${tenantCode}/ibuild-proxy/agent/send`, agentRequest);
    return response.data;
  }

  /**
   * 查询请求状态
   */
  async getAgentStatus(requestId: string): Promise<any> {
    const tenantCode = this.getTenantCode();
    const response = await api.get(`/${tenantCode}/ibuild-proxy/agent/status/${requestId}`);
    return response.data;
  }

  /**
   * 重试失败回调
   */
  async retryAgentMessage(id: number): Promise<any> {
    const tenantCode = this.getTenantCode();
    const response = await api.post(`/${tenantCode}/ibuild-proxy/agent/retry/${id}`);
    return response.data;
  }

  /**
   * 获取统计信息
   */
  async getAgentStats(): Promise<any> {
    const tenantCode = this.getTenantCode();
    const response = await api.get(`/${tenantCode}/ibuild-proxy/agent/stats`);
    return response.data;
  }

  /**
   * 获取队列统计信息
   */
  async getMQQueueStats(): Promise<any> {
    const tenantCode = this.getTenantCode();
    const response = await api.get(`/${tenantCode}/ibuild-proxy/mq/stats`);
    return response.data;
  }
}

export const ibuildProxyService = new IBuildProxyService();
export default ibuildProxyService;

